import React, { useState, useEffect } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Clock, Phone, Mail, Calendar, FileText, Users } from 'lucide-react';

const ACTIVITY_TYPES = [
  { value: 'Call', label: 'Call', icon: Phone },
  { value: 'Email', label: 'Email', icon: Mail },
  { value: 'Meeting', label: 'Meeting', icon: Calendar },
  { value: 'Site Visit', label: 'Site Visit', icon: Users },
  { value: 'Proposal Sent', label: 'Proposal Sent', icon: FileText },
  { value: 'Follow Up', label: 'Follow Up', icon: Clock },
  { value: 'Note', label: 'Note', icon: FileText },
  { value: 'Other', label: 'Other', icon: Clock },
];

const ActivityModal = ({ open, context, companies, contacts, opportunities, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    activity_type: 'Call',
    subject: '',
    description: '',
    company_id: '',
    contact_id: '',
    opportunity_id: '',
    activity_date: new Date().toISOString().split('T')[0],
    duration_minutes: null,
    outcome: '',
    follow_up_required: false,
    follow_up_date: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (context) {
      setFormData(prev => ({
        ...prev,
        company_id: context.company_id || '',
        contact_id: context.contact_id || '',
        opportunity_id: context.opportunity_id || '',
      }));
    } else {
      setFormData({
        activity_type: 'Call',
        subject: '',
        description: '',
        company_id: '',
        contact_id: '',
        opportunity_id: '',
        activity_date: new Date().toISOString().split('T')[0],
        duration_minutes: null,
        outcome: '',
        follow_up_required: false,
        follow_up_date: '',
      });
    }
  }, [context, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.subject.trim()) {
      toast.error('Subject is required');
      return;
    }

    setLoading(true);
    try {
      const dataToSend = { ...formData };
      if (!dataToSend.company_id) delete dataToSend.company_id;
      if (!dataToSend.contact_id) delete dataToSend.contact_id;
      if (!dataToSend.opportunity_id) delete dataToSend.opportunity_id;
      if (!dataToSend.duration_minutes) delete dataToSend.duration_minutes;
      if (!dataToSend.follow_up_date) delete dataToSend.follow_up_date;

      await api.crm.createActivity(dataToSend);
      toast.success('Activity logged successfully');
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log activity');
    } finally {
      setLoading(false);
    }
  };

  const filteredContacts = formData.company_id 
    ? contacts.filter(c => c.company_id === formData.company_id)
    : contacts;

  const filteredOpportunities = formData.company_id 
    ? opportunities.filter(o => o.company_id === formData.company_id)
    : opportunities;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-indigo-600" />
            Log Activity
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Activity Type</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {ACTIVITY_TYPES.map(type => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, activity_type: type.value })}
                      className={`flex flex-col items-center p-3 rounded-lg border transition-colors ${
                        formData.activity_type === type.value
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Icon className="h-5 w-5 mb-1" />
                      <span className="text-xs">{type.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="col-span-2">
              <Label>Subject *</Label>
              <Input
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="e.g., Initial discovery call with John"
                data-testid="activity-subject-input"
              />
            </div>

            <div>
              <Label>Company</Label>
              <Select
                value={formData.company_id || '__none__'}
                onValueChange={(value) => setFormData({ ...formData, company_id: value === '__none__' ? '' : value, contact_id: '', opportunity_id: '' })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No company</SelectItem>
                  {companies.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Contact</Label>
              <Select
                value={formData.contact_id || '__none__'}
                onValueChange={(value) => setFormData({ ...formData, contact_id: value === '__none__' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select contact" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No contact</SelectItem>
                  {filteredContacts.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Deal</Label>
              <Select
                value={formData.opportunity_id || '__none__'}
                onValueChange={(value) => setFormData({ ...formData, opportunity_id: value === '__none__' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select deal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No deal</SelectItem>
                  {filteredOpportunities.map(o => (
                    <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={formData.activity_date}
                onChange={(e) => setFormData({ ...formData, activity_date: e.target.value })}
              />
            </div>

            <div>
              <Label>Duration (minutes)</Label>
              <Input
                type="number"
                value={formData.duration_minutes || ''}
                onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value ? parseInt(e.target.value) : null })}
                placeholder="30"
              />
            </div>

            <div>
              <Label>Outcome</Label>
              <Input
                value={formData.outcome}
                onChange={(e) => setFormData({ ...formData, outcome: e.target.value })}
                placeholder="e.g., Scheduled follow-up"
              />
            </div>

            <div className="col-span-2">
              <Label>Description / Notes</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Detailed notes about this activity..."
                rows={4}
              />
            </div>

            <div className="col-span-2 flex items-center space-x-2">
              <Checkbox
                id="follow_up_required"
                checked={formData.follow_up_required}
                onCheckedChange={(checked) => setFormData({ ...formData, follow_up_required: checked })}
              />
              <label htmlFor="follow_up_required" className="text-sm font-medium leading-none cursor-pointer">
                Follow-up required
              </label>
            </div>

            {formData.follow_up_required && (
              <div className="col-span-2">
                <Label>Follow-up Date</Label>
                <Input
                  type="date"
                  value={formData.follow_up_date}
                  onChange={(e) => setFormData({ ...formData, follow_up_date: e.target.value })}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} data-testid="save-activity-btn">
              {loading ? 'Saving...' : 'Log Activity'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ActivityModal;
